'use client';
/**
 * MATÉRIEL — la famille (lot M1, 10/10/2026, redécoupé le même jour).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Matériel doit être une entité à part entière, comme Documents & diffusion.
 * Dedans, Stock contient tous les réactifs, y compris les contrôles et
 * calibrants, qui sont juste une sous-catégorie. Les aliquots sont visibles
 * dans l'outil Fractionnement. Les lieux de stockage vont au paramétrage. »
 * — RQ, 10/10/2026.
 *
 * Trois écrans de la famille, portés par cette enveloppe, SANS changer leur
 * modèle ni leurs données (M1 est un rangement) :
 *   · STOCK (vue `fourn`) — les lots de réactifs et consommables, et en
 *     sous-catégorie les contrôles et calibrants reconstitués. Tant que le lot
 *     commun (M3) et le raccordement des contrôles (M4) ne sont pas faits, la
 *     sous-catégorie est un segment : deux registres, une seule porte.
 *   · FRACTIONNEMENT (vue `mat`) — les aliquots, cupules et portions tirés
 *     d'un lot : déstockage, scan, dates limites.
 *   · PRÊTS & DISTRIBUTION DE TUBES (vue `stock`) — les registres de
 *     logistique, et les missions d'inventaire.
 *
 * Les `view` et leurs adresses ne changent pas : `/fournisseurs`, `/materiaux`,
 * `/stock` ouvrent chacun le bon écran. La sous-catégorie vit dans l'adresse
 * (`?onglet=`), comme sur les fiches.
 */
import React from 'react';
import { TitrePage, RefISO } from '@/components/ui';
import { useOngletAdresse } from '@/lib/useAdresse';
import { Segment } from '@/shared/Segment';
import { Lots } from '@/components/screens/Fournisseurs';
import { Materiaux } from '@/components/screens/ressources/Materiaux';
import { Stock } from '@/components/screens/Stock';
import { Fractions } from '@/components/screens/Fractions';
import { StockQuantites } from '@/components/screens/StockQuantites';

/* `quantites` (20/10/2026) : le registre des entrées, sorties et comptages
   qui remplace Boxystock — en premier, c'est ce qu'on consulte chaque jour. */
type SousCategorie = 'quantites' | 'reactifs' | 'controles';
const SOUS_CATEGORIES: readonly SousCategorie[] = ['quantites', 'reactifs', 'controles'];
type Fractionne = 'lots' | 'aliquots';
const FRACTIONNES: readonly Fractionne[] = ['lots', 'aliquots'];
type Logistique = 'prets' | 'distrib' | 'missions';
const LOGISTIQUE: readonly Logistique[] = ['prets', 'distrib', 'missions'];

export type VueMateriel = 'stock' | 'fractionnement' | 'logistique';

const eyebrow = <>Matériel · <RefISO code="6.6" prefixe="" /></>;

function StockMateriel() {
  const [sous, setSous] = useOngletAdresse<SousCategorie>('quantites', SOUS_CATEGORIES);
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <TitrePage eyebrow={eyebrow} titre="Stock" />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '90ch' }}>
        Tout ce que le laboratoire reçoit et consomme : les quantités en stock et l’inventaire, les lots de réactifs et de consommables, et — en
        sous-catégorie — les contrôles et calibrants reconstitués. Les fournisseurs sont au carnet de
        contacts ; les aliquots se suivent dans <strong>Fractionnement</strong>.
      </p>
      <Segment ariaLabel="Sous-catégorie du stock" valeur={sous} onChange={setSous}
        options={[
          { valeur: 'quantites', libelle: 'Quantités & inventaire' },
          { valeur: 'reactifs', libelle: 'Réactifs & consommables' },
          { valeur: 'controles', libelle: 'Contrôles & calibrants' },
        ]} />
      {sous === 'quantites' && <StockQuantites />}
      {sous === 'reactifs' && <Lots />}
      {sous === 'controles' && <Materiaux encastre onglet="flacons" />}
    </div>
  );
}

/* Deux registres sous un même mot (M3, 10/10/2026) : les FRACTIONS DES LOTS
   (portions, cupules, aliquots de tout produit fractionnable du catalogue) et
   les ALIQUOTS DE CONTRÔLE des flacons reconstitués — qui rejoindront le
   premier quand les contrôles seront raccordés au catalogue (M4). */
function Fractionnement() {
  const [onglet, setOnglet] = useOngletAdresse<Fractionne>('lots', FRACTIONNES);
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <TitrePage eyebrow={eyebrow} titre="Fractionnement" />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '90ch' }}>
        Les fractions tirées d’un lot — portions de réactif, cupules de calibrant, aliquots — pour tout produit
        que le catalogue déclare fractionnable : sortie au scan, date limite calculée (la première borne échue
        fait foi), rebut motivé. Les aliquots des flacons de contrôle reconstitués gardent leur registre.
      </p>
      <Segment ariaLabel="Registre de fractionnement" valeur={onglet} onChange={setOnglet}
        options={[
          { valeur: 'lots', libelle: 'Fractions des lots' },
          { valeur: 'aliquots', libelle: 'Aliquots de contrôle' },
        ]} />
      {onglet === 'lots' && <Fractions />}
      {onglet === 'aliquots' && <Materiaux encastre onglet="aliquots" />}
    </div>
  );
}

function LogistiqueMateriel() {
  const [tab, setTab] = useOngletAdresse<Logistique>('prets', LOGISTIQUE);
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <TitrePage eyebrow={<>Matériel · <RefISO code="6.6.4" prefixe="" /></>} titre="Prêts & distribution de tubes" />
      <Segment ariaLabel="Registre de logistique" valeur={tab} onChange={setTab}
        options={[
          { valeur: 'prets', libelle: 'Prêts de produits' },
          { valeur: 'distrib', libelle: 'Distribution de tubes' },
          { valeur: 'missions', libelle: 'Missions d’inventaire' },
        ]} />
      <Stock encastre tab={tab} />
    </div>
  );
}

export function Materiel({ vue = 'stock' }: { vue?: VueMateriel } = {}) {
  if (vue === 'fractionnement') return <Fractionnement />;
  if (vue === 'logistique') return <LogistiqueMateriel />;
  return <StockMateriel />;
}
